/**
 * Response shape for `GET /api/audit/login-events` (`server/src/audit/loginEventsRoutes.ts`) — a
 * login/logout audit viewer, on direct user instruction, 2026-08-18: "we need a login / logout log".
 *
 * Login and logout have always been audited (`auth/service.ts`, `invites/service.ts`,
 * `auth/sessionsService.ts` all write `action: 'login'`/`action: 'logout'` events); this is the first
 * route that reads any of it back out. Deliberately narrower than the still-open, general
 * practice-wide audit-log viewer `emr-po/BACKLOG.md` tracks separately (every PHI read/write, not
 * just auth events) — this shape exists for the auth-events slice only.
 *
 * Same discipline as `sessionRequests.ts`'s `sessionListItemSchema`: every object schema is strict,
 * and `occurredAt` is `z.string()` rather than `z.iso.datetime()` — matching
 * `platformRequests.ts`'s `platformAuditLogEntrySchema.occurredAt`'s own comment on why: it is
 * DB-echoed straight off `audit_events.occurred_at`, a Postgres `timestamptz` whose text form
 * (`web/src/format/serverInstant.ts`'s own header explains the wire shape in full) is not guaranteed
 * to satisfy `z.iso.datetime()`'s stricter ISO 8601 check.
 */

import { z } from 'zod';

export const loginAuditActionSchema = z.enum(['login', 'logout']);
export type LoginAuditAction = z.infer<typeof loginAuditActionSchema>;

/**
 * One login or logout event, scoped to the caller's own org
 * (`server/src/audit/loginEventsRoutes.ts` enforces the `org_id` filter itself — `audit_events`
 * carries no row-level-security policy at all, `server/src/db/schema/audit.ts`'s own header comment
 * explains why).
 */
export const loginAuditEventSchema = z.strictObject({
  /** `audit_events.id` — included purely as a stable row key for a client-side list; nothing about
   * this route's own tenant/capability scoping depends on it being unguessable. */
  id: z.uuid(),
  occurredAt: z.string(),
  actorUserId: z.uuid(),
  /** Resolved from `users.email`, the same join `orgs/service.ts`'s `findOrgMember` already uses for
   * a colleague's own identity. `null` only in the defensive case where the referenced user row is
   * somehow gone — every real login/logout event's actor is a user this app itself created. */
  actorEmail: z.string().nullable(),
  action: loginAuditActionSchema,
  /** `'denied'` is real and worth surfacing here — e.g. a concurrent login losing an MFA-code race
   * (`auth/service.ts`'s `login()`, the `mfa_race_lost` case) is scoped to the real org and shows up
   * in this view. A denied login *before* any org is known (a bad password, an unenrolled MFA code)
   * is written to the nil-org shard instead (`auth/service.ts`'s `UNSCOPED_AUDIT_ID`) and never
   * appears in any org-scoped view — a deliberate property of that shard, not a gap in this route. */
  outcome: z.enum(['allowed', 'denied']),
  /** Server-resolved at the time of the event (`auth/middleware.ts`'s `resolveRequestMeta`), never
   * client-asserted after the fact. */
  sourceIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  /** Field-name tags a handful of call sites attach for context — `['switch_org']`,
   * `['revoke_all']`, `['mfa_race_lost']`, or a failed-login reason name. Never a value, matching
   * every other audit event's `fields` (`@emr/shared`'s `AuditEvent.fields` comment). Empty for an
   * ordinary login/logout. */
  fields: z.array(z.string()),
});
export type LoginAuditEvent = z.infer<typeof loginAuditEventSchema>;

export const loginAuditEventsResponseSchema = z.strictObject({
  events: z.array(loginAuditEventSchema),
});
export type LoginAuditEventsResponse = z.infer<typeof loginAuditEventsResponseSchema>;
